import { ref, computed, watch, shallowRef, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import { API, HOST } from '@/utils/http'
import { toast } from '@/components/ui/toast'
import { mergePolygons } from '@/utils/geometry'

/**
 * Shared composable for loading features feed + dates.
 * Used by both Project/index.vue (with projectId) and DetailCollectedData.vue (with layerCode).
 *
 * @param {Object} options
 * @param {import('vue').Ref|import('vue').ComputedRef} options.orgId
 * @param {import('vue').Ref|import('vue').ComputedRef|null} options.projectId
 * @param {import('vue').Ref|import('vue').ComputedRef|null} options.layerCode
 * @param {import('vue').ComputedRef|null} [options.additionalParams] - extra params per context
 */
export function useFeaturesFeed({ orgId, projectId, layerCode, additionalParams }) {
  const store = useStore()

  const total = ref(0)
  const page = ref(1)
  const limit = ref(15)
  const offset = computed(() => (page.value - 1) * limit.value)
  const isLoading = ref(false)

  const viewportOnly = ref(localStorage.getItem('viewportOnly') === 'true')
  const showFilters = ref(false)
  const currentFilters = ref(null)

  const map = computed(() => store.getters['geo/getMap'])
  const selectedRange = computed(() => store.state.layers.selectedRange)
  const feedFeatures = computed(() => store.getters['geo/getFeedFeatures'])

  const previousFeatures = shallowRef(null)
  const displayFeatures = computed(() => {
    const current = feedFeatures.value.features
    if (current && current.length > 0) return current
    if (isLoading.value && previousFeatures.value) return previousFeatures.value
    return current
  })

  const hasActiveFilters = computed(() => {
    const hasDateRange = selectedRange.value && selectedRange.value.length === 2
    const hasProjectFilters = currentFilters.value && (
      (currentFilters.value.templateIds?.length > 0) ||
      (currentFilters.value.memberIds?.length > 0) ||
      (currentFilters.value.searchText?.trim()) ||
      currentFilters.value.isPublicData ||
      (currentFilters.value.geometryTypes?.length > 0) ||
      (currentFilters.value.geometries?.length > 0)
    )
    return hasDateRange || viewportOnly.value || hasProjectFilters
  })

  // Track previous features for smooth loading transitions
  watch(
    () => feedFeatures.value.features,
    (newFeatures) => {
      if (newFeatures && newFeatures.length > 0) {
        previousFeatures.value = newFeatures
      }
    }
  )

  const fetchDates = async () => {
    const org = orgId.value
    const pid = projectId?.value
    const lc = layerCode?.value
    if (!org || (!pid && !lc)) return

    try {
      const params = {}
      if (pid) params.project_id = pid
      if (lc) params.layer_code = lc

      const response = await API({
        method: 'GET',
        url: `${HOST}/organization/${org}/features/dates`,
        params
      })

      if (response.data?.layers && Array.isArray(response.data.layers)) {
        const processedLayers = response.data.layers.map(layer => ({
          id: layer.id || 'unknown',
          name: layer.name || 'Unknown Template',
          color: layer.color || 'blue',
          dates: Array.isArray(layer.dates) ? layer.dates : []
        }))

        if (processedLayers.length > 0) {
          store.commit('layers/ADD_DATES', processedLayers)
        }
      }
    } catch (error) {
      console.error('Error fetching dates:', error)
    }
  }

  const fetchFeatures = async () => {
    const org = orgId.value
    const pid = projectId?.value
    const lc = layerCode?.value
    if (!org || (!pid && !lc)) return

    isLoading.value = true
    try {
      const params = {
        limit: limit.value,
        offset: offset.value,
      }

      if (pid) params.project_id = pid
      if (lc) params.layer_code = lc

      // Date range from timeline
      const [startDate, endDate] = selectedRange.value || []
      if (startDate && endDate) {
        params.startDate = startDate
        params.endDate = endDate
      }

      // Viewport bounds
      if (viewportOnly.value && map.value) {
        const bounds = map.value.getBounds()
        params.west = bounds.getWest()
        params.south = bounds.getSouth()
        params.east = bounds.getEast()
        params.north = bounds.getNorth()
      }

      // ProjectFilters params
      if (currentFilters.value) {
        if (currentFilters.value.templateIds?.length > 0) {
          params.template_ids = currentFilters.value.templateIds.join(',')
        }
        if (currentFilters.value.memberIds?.length > 0) {
          params.member_ids = currentFilters.value.memberIds.join(',')
        }
        if (currentFilters.value.searchText?.trim()) {
          params.search_text = currentFilters.value.searchText
        }
        if (currentFilters.value.isPublicData) {
          params.is_public = true
        }
        if (currentFilters.value.geometryTypes?.length > 0) {
          params.geometry_types = currentFilters.value.geometryTypes.join(',')
        }
        if (currentFilters.value.geometries?.length > 0) {
          const merged = mergePolygons(currentFilters.value.geometries)
          if (merged) {
            params.geometry_filter = JSON.stringify(merged)
          }
        }
      }

      // Context-specific additional params (e.g. dataFilter's shared/member_id)
      if (additionalParams?.value) {
        Object.assign(params, additionalParams.value)
      }

      const response = await API({
        method: 'GET',
        url: `${HOST}/organization/${org}/features`,
        params
      })

      if (response.data?.type === 'FeatureCollection') {
        store.commit('geo/SET_FEED_FEATURES', {
          features: response.data
        })
        total.value = response.data.total ?? response.data.features?.length ?? 0
      }
    } catch (e) {
      console.log(e)
      if (e.response?.status === 403) {
        toast({ title: `You don't have access to this data` })
      } else if (e.response?.status === 404) {
        toast({ title: `Data not found` })
      } else {
        throw e
      }
    } finally {
      isLoading.value = false
    }
  }

  const toggleViewportOnly = async (value) => {
    viewportOnly.value = value
    localStorage.setItem('viewportOnly', value)
    page.value = 1
    await fetchFeatures()
  }

  const updateFilters = (filters) => {
    currentFilters.value = filters
    store.commit('geo/SET_PROJECT_FILTERS', filters)
    page.value = 1
    fetchFeatures()
    fetchDates()
  }

  const paginationUpdated = (pageCount) => {
    page.value = pageCount
    fetchFeatures()
  }

  const resetState = () => {
    total.value = 0
    page.value = 1
    currentFilters.value = null
    store.commit('geo/SET_FEED_FEATURES', { features: { type: 'FeatureCollection', features: [] } })
    previousFeatures.value = null
  }

  // Watch selectedRange → refetch (debounced to coalesce rapid range changes)
  let selectedRangeTimeout = null
  watch(selectedRange, () => {
    if (selectedRangeTimeout) clearTimeout(selectedRangeTimeout)
    selectedRangeTimeout = setTimeout(() => {
      page.value = 1
      fetchFeatures()
    }, 300)
  })

  onUnmounted(() => {
    if (selectedRangeTimeout) clearTimeout(selectedRangeTimeout)
  })

  return {
    // State
    total,
    page,
    limit,
    offset,
    isLoading,
    viewportOnly,
    showFilters,
    currentFilters,
    hasActiveFilters,
    displayFeatures,
    previousFeatures,
    selectedRange,

    // Methods
    fetchFeatures,
    fetchDates,
    toggleViewportOnly,
    updateFilters,
    paginationUpdated,
    resetState,
  }
}
